import { useCallback, useMemo, useState } from "react";
import { useIdentity } from "@/features/identity/hooks/useIdentity";
import { useUnsavedWarning } from "@/features/identity/hooks/useUnsavedWarning";
import { hasErrors } from "@/features/identity/validation";
import { friendlyDataError } from "@/lib/errors";

export const IDLE = { state: "idle", message: null };

const omit = (obj, key) => {
  if (!(key in obj)) return obj;
  const next = { ...obj };
  delete next[key];
  return next;
};

export function useSectionForm(section) {
  const { user, profile, saveProfile } = useIdentity();
  const initial = useMemo(() => section.fromProfile(profile, user), [section, profile, user]);
  const [values, setValues] = useState(initial);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState(IDLE);
  const dirty = Object.keys(initial).some((k) => values[k] !== initial[k]);
  useUnsavedWarning(dirty);

  const setField = useCallback((name, value) => {
    setValues((v) => ({ ...v, [name]: value }));
    setErrors((e) => omit(e, name));
    setStatus(IDLE);
  }, []);
  const onChange = useCallback((e) => setField(e.target.name, e.target.value), [setField]);

  const submit = async ({ force = false } = {}) => {
    const found = section.validate(values);
    setErrors(found);
    if (hasErrors(found)) {
      setStatus({ state: "invalid", message: "Please check the highlighted fields." });
      return false;
    }
    if (!dirty && !force) return true;
    setStatus({ state: "saving", message: null });
    try {
      const updated = await saveProfile(section.toPatch(values));
      setValues(section.fromProfile(updated, user));
      setStatus({ state: "saved", message: "Saved." });
      return true;
    } catch (err) {
      setStatus({ state: "error", message: friendlyDataError(err, "We couldn't save these changes. Please try again.") });
      return false;
    }
  };

  return { values, errors, status, dirty, onChange, setField, submit };
}
